import {useEffect, useRef, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {fetchBookmarkAheadPlacesFeed} from '../../endpoints/api';
import {Place} from '../../types/article';

export function useBookmarkPlacesFeedState(params?: {
  latitude?: number;
  longitude?: number;
}) {
  // Mirrors useBookmarkAheadFeed: static query key + ref-captured params so the
  // queryFn always reads the latest coords without busting the cache on every render.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const {data, isLoading: loading, isFetching: refetching} = useQuery(
    ['bookmark-places-feed'],
    () => fetchBookmarkAheadPlacesFeed(paramsRef.current),
    {staleTime: 5 * 60 * 1000},
  );

  const sections = data?.data?.data;
  const nearestYou: Place[] = sections?.nearest_you ?? [];
  const popularNeighbors: Place[] = sections?.popular_with_neighbors ?? [];
  const popularSanDiego: Place[] = sections?.popular_in_city ?? [];

  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState(0);

  useEffect(() => {
    const el = bannerRef.current;
    if (!el) {
      setBannerHeight(0);
      return;
    }
    // offsetHeight (border-box) — contentRect.height excludes padding and leaves
    // section headers pinning behind the banner.
    const observer = new ResizeObserver(() => {
      setBannerHeight(Math.floor(el.offsetHeight));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return {
    nearestYou,
    popularNeighbors,
    popularSanDiego,
    loading,
    refetching,
    bannerRef,
    bannerHeight,
  };
}
